/**
 * DataTable — generic table component for sub-route detail pages.
 *
 * Features:
 *   - Search (client-side filter on specified field)
 *   - Sortable columns
 *   - Pagination (10/25/50/100)
 *   - Row click → fires onRowClick(row) for drawer
 *   - Status badges with tone mapping
 *   - Loading skeleton
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Search, Loader2 } from "lucide-react";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  tone?: (row: T) => "default" | "destructive" | "outline" | "secondary";
}

export interface DataTableProps<T> {
  title: string;
  subtitle?: string;
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  searchFields?: (keyof T)[];
  pageSize?: number;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  actions?: React.ReactNode;
}

export function DataTable<T extends Record<string, unknown>>({
  title, subtitle, data, columns, loading, searchFields, pageSize = 25, onRowClick, emptyMessage = "Aucun résultat", actions,
}: DataTableProps<T>) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [size, setSize] = useState(pageSize);

  const filtered = useMemo(() => {
    if (!search || !searchFields) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      searchFields.some((f) => String(row[f] ?? "").toLowerCase().includes(q))
    );
  }, [data, search, searchFields]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = (av as any) > (bv as any) ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const pages = Math.ceil(total / size) || 1;
  const slice = sorted.slice(page * size, page * size + size);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
          </div>
        </div>
        {searchFields && searchFields.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
              />
            </div>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={size}
              onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : slice.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">{emptyMessage}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-white/5 text-xs uppercase text-muted-foreground">
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={String(c.key)}
                        className={`py-2 px-3 text-left font-medium ${c.sortable ? "cursor-pointer hover:text-foreground" : ""} ${c.width || ""}`}
                        onClick={() => c.sortable && handleSort(String(c.key))}
                      >
                        <div className="flex items-center gap-1">
                          {c.label}
                          {c.sortable && sortKey === String(c.key) && (
                            <span className="text-[10px]">{sortDir === "asc" ? "▲" : "▼"}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slice.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-white/5 hover:bg-white/[0.02] ${onRowClick ? "cursor-pointer" : ""}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {columns.map((c) => {
                        const tone = c.tone?.(row);
                        const content = c.render ? c.render(row) : String(row[c.key as keyof T] ?? "");
                        return (
                          <td key={String(c.key)} className="py-2 px-3">
                            {tone ? <Badge variant={tone}>{content}</Badge> : content}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <div>
                {total} résultat{total > 1 ? "s" : ""} · page {page + 1} / {pages}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}